import React from "react";
import PhoneNumberInput, {
  getCountryCallingCode,
} from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import el from "react-phone-number-input/locale/el.json";
import en from "react-phone-number-input/locale/en.json";

import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "../../../shadcn/components/ui/input-group";
import { Input } from "../../../shadcn/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../shadcn/components/ui/select";
import { useTranslation } from "../../../context/Internationalization";
import {
  PHONE_COUNTRIES,
  countryOf,
  isCallingCodeOnly,
  toE164,
  toInternational,
} from "./phoneNumbers";

// The selected country, for typed or pasted numbers without a "+".
const CountryContext = React.createContext(undefined);

// The input. Text without a "+" ("6912345678", "0035799123456") becomes an
// international number before the library reads it, so a pasted number keeps
// its country and local digits get the selected country's code.
const NumberInput = React.forwardRef(function NumberInput(
  { onChange, ...props },
  ref
) {
  const country = React.useContext(CountryContext);
  return (
    <InputGroupInput
      ref={ref}
      {...props}
      onChange={(event) => {
        const text = event.target.value;
        const international = toInternational(text, country);
        if (international !== text) event.target.value = international;
        onChange(event);
      }}
    />
  );
});

const Flag = ({ country, label }) => {
  const Icon = country ? flags[country] : null;
  return (
    <span className="inline-flex h-4 w-6 shrink-0 overflow-hidden rounded-sm bg-muted [&_svg]:size-full">
      {Icon ? <Icon title={label} /> : null}
    </span>
  );
};

const CountrySelect = ({
  value,
  onChange,
  options,
  disabled,
  readOnly,
  "aria-label": ariaLabel,
}) => {
  const countries = options.filter((option) => option.value);
  const labelOf = (country) =>
    countries.find((option) => option.value === country)?.label;
  return (
    <InputGroupAddon>
      <Select
        value={value ?? null}
        onValueChange={(country) => onChange(country || undefined)}
        disabled={disabled || readOnly}
      >
        <SelectTrigger
          size="sm"
          aria-label={ariaLabel}
          className="h-7 gap-1 border-0 bg-transparent px-1.5 shadow-none"
        >
          <SelectValue>
            {(country) => <Flag country={country} label={labelOf(country)} />}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="min-w-max">
          {countries.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              <Flag country={option.value} />
              {option.label} +{getCountryCallingCode(option.value)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </InputGroupAddon>
  );
};

const Container = React.forwardRef(function Container(
  { className, style, children },
  ref
) {
  return (
    <InputGroup ref={ref} style={style}>
      {children}
    </InputGroup>
  );
});

// shadcn phone field (replaces mui-tel-input). `onChange` receives the number
// as "+306912345678", or "" when empty. Other props go to the <input>.
const PhoneInput = React.forwardRef(function PhoneInput(
  {
    value,
    onChange,
    defaultCountry = "GR",
    onlyCountries = PHONE_COUNTRIES,
    style,
    ...inputProps
  },
  ref
) {
  const { lang, t } = useTranslation();
  const e164 = toE164(value, defaultCountry);
  const [country, setCountry] = React.useState(defaultCountry);
  // A stored value that is not a phone number is shown as plain text. Only a
  // value from the parent decides this, never typing: switching input types
  // while typing would drop focus.
  const isPlain = (next) => Boolean(next) && !toE164(next, defaultCountry);
  const [plain, setPlain] = React.useState(() => isPlain(value));
  // What the field shows. While only a calling code is typed ("+357") the
  // parent gets "", but the field keeps "+357" and its country: passing ""
  // back would reset it to the default country mid-typing.
  const [draft, setDraft] = React.useState(e164);
  const [sent, setSent] = React.useState(value ?? "");
  if ((value ?? "") !== sent) {
    // A new value from the parent, not the one we sent: show it.
    setSent(value ?? "");
    setDraft(e164);
    setPlain(isPlain(value));
  }
  // Follow the country of the number in the field (a stored Cypriot number
  // too, not only a picked one), so local digits typed over it stay Cypriot.
  const draftCountry = countryOf(draft);
  if (draftCountry && draftCountry !== country) setCountry(draftCountry);

  if (plain) {
    return (
      <Input
        ref={ref}
        {...inputProps}
        style={style}
        value={value ?? ""}
        onChange={(event) => {
          setSent(event.target.value);
          onChange?.(event.target.value);
        }}
      />
    );
  }

  // The calling code stays editable: pasting "+357…" over "+30" switches to
  // Cyprus (and back), like mui-tel-input did.
  return (
    <CountryContext.Provider value={country}>
      <PhoneNumberInput
        ref={ref}
        {...inputProps}
        style={style}
        international
        addInternationalOption={false}
        defaultCountry={defaultCountry}
        countries={onlyCountries}
        labels={{ ...(lang === "gr" ? el : en), country: t("phone_country") }}
        value={draft}
        onChange={(next) => {
          // Only a calling code ("+30") is still an empty field.
          const number = isCallingCodeOnly(next, onlyCountries)
            ? ""
            : next ?? "";
          setDraft(next);
          setSent(number);
          onChange?.(number);
        }}
        containerComponent={Container}
        countrySelectComponent={CountrySelect}
        inputComponent={NumberInput}
        onCountryChange={(next) => next && setCountry(next)}
      />
    </CountryContext.Provider>
  );
});

export default PhoneInput;
